import { Grid } from '@elliemae/ds-grid';
import { IField, IFieldGroup, IScreenGroup } from '@elliemae/ns-schema';
import _ from 'lodash';
import {
  applyClientLookupsToSchema,
  DraggableField,
  EmptySchema,
  mapEppsCustomFields,
  NSSampleSchema,
  sampleClientLookups,
  SchemaAndRules,
  getAllDraggableFields,
  getDraggableFieldList,
  NSSampleCustomFields,
  EppsCustomField,
} from '@frontend/pricing-search';
import { memo, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Outlet } from 'react-router-dom';
import {
  getAllLookups,
  getClientSchema,
  getEPPSCustomFields,
} from '../../../api/eppsService';
import { CustomToastType, triggerToast } from '../../../utils/toasthelper';
import { EmptyCustomFieldScreen, SampleConfig } from '../constants/config';
import { useAppStore, useSchemaStore } from '../store';
import Header from './global-header';
import FullScreenSpinner from '../components/misc/fullScreenSpinner';
import { EnvNotificationHeader } from '../../common/envNotificationHeader';

const removeExistingFieldsInSchemaFromList = (
  config: SchemaAndRules | null,
  fieldList: DraggableField[],
) => {
  // Extract existing model paths from the current schema
  const existingComponentIds = config?.schema?.screens
    ?.filter((screen) => screen.type === 'screen')
    ?.reduce((acc: IField[], screen) => {
      const fields = (screen.fields as IFieldGroup[]).reduce(
        (fieldAcc: IField[], fieldGroup) => {
          return [...fieldAcc, ...((fieldGroup.fields as IField[]) || [])];
        },
        [],
      );
      return [...acc, ...(fields || [])];
    }, [])
    ?.map((field) => field.component_id);

  // Filter the field list
  const filteredList = fieldList.filter(
    (i) => !existingComponentIds?.includes(i.schema.component_id),
  );
  return filteredList;
};

export const PricingSearchLayout = memo(() => {
  const toggleFullScreenLoadingSpinnerVisibility = useAppStore(
    (state) => state.toggleFullScreenLoadingSpinnerVisibility,
  );
  const setFieldListStore = useSchemaStore((state) => state.setFieldList);
  const setAllStandardFieldsStore = useSchemaStore(
    (state) => state.setAllStandardFields,
  );
  const setAllCustomFieldsStore = useSchemaStore(
    (state) => state.setAllCustomFields,
  );
  const useLocalData = useAppStore((state) => state.useLocalTestData);
  const chokeConsoleWarnings = useAppStore(
    (state) => state.chokeConsoleWarnings,
  );
  const allStandardFields = useSchemaStore((state) => state.allStandardFields);
  const allCustomFields = useSchemaStore((state) => state.allCustomFields);
  const setClientConfigs = useSchemaStore((state) => state.setClientConfigs);
  const getSelectedChannel = useSchemaStore((state) => state.selectedChannel);
  const clientConfigs = useSchemaStore((state) => state.clientConfigs);
  const setWorkingSchema = useSchemaStore((state) => state.setWorkingSchema);
  const setClientLookups = useSchemaStore((state) => state.setClientLookups);
  const workingSchema = useSchemaStore((state) => state.workingSchema);
  const defaultSchemaWithCustomFields = useSchemaStore(
    (state) => state.defaultSchemaWithCustomFields,
  );
  const setDefaultSchemaWithCustomFields = useSchemaStore(
    (state) => state.setDefaultSchemaWithCustomFields,
  );

  useEffect(() => {
    const effect = async () => {
      // Set current schema state
      const clientSchemaResponse = await getClientSchema().catch((error) => {
        triggerToast(
          CustomToastType.ERROR,
          'Schema Error',
          'Current client schema could not be retrieved. Please try again.',
        );
      });
      // Get lookups
      const clientLookupsResponse = await getAllLookups().catch((error) => {
        triggerToast(
          CustomToastType.ERROR,
          'Client Lookups Error',
          'Current client lookups could not be retrieved. Please try again.',
        );
      });
      const filteredSchemaOptionsWithLookups = clientSchemaResponse?.map(
        (channelSchema) => {
          if (channelSchema.draftSchema && clientLookupsResponse) {
            applyClientLookupsToSchema(
              channelSchema.draftSchema,
              clientLookupsResponse,
            );
          }
          return channelSchema;
        },
      );
      if (filteredSchemaOptionsWithLookups) {
        setClientConfigs(
          filteredSchemaOptionsWithLookups?.length
            ? filteredSchemaOptionsWithLookups
            : SampleConfig,
        );
        // Get list of custom fields from active client
        const customFieldsResponse = await getEPPSCustomFields().catch(
          (error) => {
            triggerToast(
              CustomToastType.ERROR,
              'Custom Field Error',
              'Current client custom fields could not be retrieved. Please try again.',
            );
          },
        );

        if (
          customFieldsResponse &&
          filteredSchemaOptionsWithLookups &&
          clientLookupsResponse
        ) {
          const mappedFields = mapEppsCustomFields(customFieldsResponse).map(
            (i) => ({ schema: i }),
          );
          setAllCustomFieldsStore(mappedFields);
          setAllStandardFieldsStore(
            getDraggableFieldList(clientLookupsResponse),
          );

          const defaultSchema = applyClientLookupsToSchema(
            JSON.parse(JSON.stringify(NSSampleSchema)),
            clientLookupsResponse,
          );
          const customFieldScreen = EmptyCustomFieldScreen;
          customFieldScreen.fields[0].fields = mappedFields.map(
            (i) => i.schema,
          );
          defaultSchema.schema.screens.splice(2, 0, customFieldScreen);
          (defaultSchema.schema.screens[0] as IScreenGroup).screens.splice(
            1,
            0,
            {
              id: customFieldScreen.id,
            },
          );

          setDefaultSchemaWithCustomFields(defaultSchema);
          toggleFullScreenLoadingSpinnerVisibility(false);
        }
      }
    };

    if (useLocalData) {
      setClientLookups(sampleClientLookups);
      setAllStandardFieldsStore(getAllDraggableFields());
      const mappedFields = mapEppsCustomFields(NSSampleCustomFields).map(
        (i) => ({ schema: i }),
      );
      setAllCustomFieldsStore(mappedFields);
      setClientConfigs(SampleConfig);
      const defaultSchema = applyClientLookupsToSchema(
        JSON.parse(JSON.stringify(NSSampleSchema)),
        sampleClientLookups,
      );
      setDefaultSchemaWithCustomFields(defaultSchema);
      toggleFullScreenLoadingSpinnerVisibility(false);
    } else {
      effect();
    }
  }, []);

  useEffect(() => {
    if (chokeConsoleWarnings && process.env.NODE_ENV === 'development') {
      console.warn = () => {};
    }
  }, [chokeConsoleWarnings]);

  // Set working schema based on selected channel
  useEffect(() => {
    const channel =
      clientConfigs.find((i) => i.channel === getSelectedChannel.value) || null;
    if (channel) {
      const baseSchema = (channel.draftSchema ||
        channel?.publishedSchema ||
        defaultSchemaWithCustomFields) as SchemaAndRules;

      const allSchemaRules = _.uniqBy(
        clientConfigs
          .flatMap(
            (c) => (c.draftSchema ?? c.publishedSchema)?.schemaRules ?? [],
          )
          .sort((a, b) => (b.modifiedOn > a.modifiedOn ? 1 : -1)),
        'id',
      );

      setWorkingSchema({ ...baseSchema, schemaRules: allSchemaRules });
    } else {
      setWorkingSchema(EmptySchema);
    }
  }, [getSelectedChannel, clientConfigs, defaultSchemaWithCustomFields]);

  // Update field list on workingSchema change
  useEffect(() => {
    const initFieldList = [...allStandardFields, ...allCustomFields];
    const filteredFields = removeExistingFieldsInSchemaFromList(
      workingSchema,
      initFieldList,
    );
    setFieldListStore(filteredFields);
  }, [workingSchema, allCustomFields, allStandardFields]);

  const isValidWorkingSchema =
    workingSchema && Object.keys(workingSchema).length > 0;

  return (
    <>
      <Helmet defaultTitle="ICE Rate Management Application">
        <meta name="description" content="ICE Rate Management Application" />
      </Helmet>
      <Grid
        style={{ width: '100%' }}
        height="100vh"
        rows={['auto', '1fr']}
        data-testid="page-layout"
      >
        {window.self === window.top && (
          <Grid>
            <EnvNotificationHeader />
            <Header />
          </Grid>
        )}
        <Grid>
          <main>
            {isValidWorkingSchema ? <Outlet /> : <FullScreenSpinner />}
          </main>
        </Grid>
      </Grid>
    </>
  );
});
